import { countyToMetro, stateNameToCode } from "./yourLookupMaps";

/** Copy address fields and compute stateCode/metro like before */
function extractFields(address = {}) {
  const result = {};

  // 1) copy every field from address (undefined → null)
  for (const [key, value] of Object.entries(address)) {
    result[key] = value ?? null;
  }

  // 2) state code
  const stateCode = address.state ? stateNameToCode[address.state] : null;
  result.stateCode = stateCode;

  // 3) metro (prefer server-provided; else fall back to county→metro map)
  if (address.metro) {
    result.metro = address.metro;
    result.metroKey = null; // server supplied metro, key not needed
  } else {
    const metroKey =
      address.county && stateCode ? `${address.county}, ${stateCode}` : null;
    result.metroKey = metroKey;
    result.metro = metroKey ? countyToMetro[metroKey] || null : null;
  }

  return result;
}

/** Turn the new API response into the old normalized object */
function parseReverseGeocodeApi(data) {
  const raw = data && data.result;
  if (!raw)
    throw new Error("Malformed reverse geocode response (missing result)");

  const out = extractFields(raw.address || {});
  out.propertyId = raw.osm_id ?? null; // you used to add this
  out.placeId = raw.place_id ?? null; // optional but handy
  out.displayName = raw.display_name ?? null; // optional but handy
  return out;
}

/**
 * Reverse geocode a single coordinate pair via POST /reverse-geocode.
 * Rounds to 4 decimals to align with server-side caching (~11m grid).
 *
 * @param {number|string} lat
 * @param {number|string} lon
 * @returns {Promise<object>} geopy raw result (includes address.metro)
 */
export async function reverseGeocode(lat, lon) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiKey = process.env.REACT_APP_API_KEY;
  const endpoint = `${apiUrl.replace(/\/+$/, "")}/reverse-geocode`;

  const toNum = (v) => (typeof v === "number" ? v : Number(v));
  const round4 = (n) => Math.round(n * 1e4) / 1e4;

  const latNum = toNum(lat);
  const lonNum = toNum(lon);
  if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) {
    console.error({ lat, lon, latNum, lonNum });
    throw new Error(`Invalid coordinates: lat=${lat} lon=${lon}`);
  }

  const lat4 = round4(latNum);
  const lon4 = round4(lonNum);

  // Helpful for debugging cache keys
  console.log("ReverseGeocode → payload:", { lat: lat4, lon: lon4 });

  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // Use lowercase header that your API Gateway CORS allows
      react_app_api_key: apiKey,
    },
    body: JSON.stringify({ lat: lat4, lon: lon4 }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Reverse geocode API error: ${res.status} ${errorText}`);
  }

  const data = await res.json();
  console.log("Reverse geocode raw response:", data);

  if (!data || typeof data !== "object" || !data.result) {
    throw new Error("Malformed reverse geocode response.");
  }

  // Return the same normalized shape as before
  return parseReverseGeocodeApi(data);
}
